'use client';

import { motion } from 'motion/react';

import { cn } from '@/lib/utils';

import { useSim } from './use-sim';

/**
 * The breaker, played: requests stream at Provider 1, three failures land in
 * the rolling window, the breaker opens and traffic bends to Provider 2, then
 * the failures age out and traffic returns. Pure function of the tick.
 */

// One entry per tick: which provider serves it and whether it fails.
const SCRIPT = [
  { to: 1, fail: false },
  { to: 1, fail: false },
  { to: 1, fail: true },
  { to: 1, fail: false },
  { to: 1, fail: true },
  { to: 1, fail: true }, // 3 fails in window -> opens
  { to: 2, fail: false },
  { to: 2, fail: false },
  { to: 2, fail: false },
  { to: 2, fail: false }, // fails aging out
  { to: 2, fail: false },
  { to: 1, fail: false }, // closed again
  { to: 1, fail: false },
  { to: 1, fail: false },
] as const;

const WINDOW = 6;

function stateAt(tick: number) {
  const served = SCRIPT.slice(0, Math.min(tick, SCRIPT.length));
  const windowSlice = served.slice(-WINDOW);
  const failsInWindow = windowSlice.filter((s) => s.to === 1 && s.fail).length;
  const current = SCRIPT[Math.min(tick, SCRIPT.length - 1)];
  return { served, windowSlice, failsInWindow, current };
}

export function BreakerSim() {
  const { ref, tick, reduceMotion } = useSim({
    totalTicks: SCRIPT.length + 2,
    msPerTick: 550,
    loop: true,
  });

  // Reduced motion: freeze on the most informative frame, the open breaker.
  const t = reduceMotion ? 6 : Math.min(tick, SCRIPT.length);
  const { windowSlice, failsInWindow } = stateAt(t);
  const open = failsInWindow >= 3;

  return (
    <div
      ref={ref}
      className="mt-4 border border-edge bg-muted/20 p-4"
      role="img"
      aria-label="Simulation: after three failures in a sixty second window, requests route to the next provider until the failures age out of the window."
    >
      <div className="grid grid-cols-[auto_1fr_1fr] items-start gap-3 sm:gap-5">
        <div className="pt-1 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          requests
          <span className="mt-1 block normal-case tracking-normal">
            {!reduceMotion && t < SCRIPT.length && (
              <motion.span
                key={t}
                initial={{ x: 0, opacity: 0 }}
                animate={{ x: 14, opacity: [0, 1, 1, 0.4] }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="inline-block size-1.5 rounded-full bg-foreground"
              />
            )}
          </span>
        </div>

        {/* Provider 1 with its rolling window */}
        <div
          className={cn(
            'rounded-lg border p-3 transition-colors',
            open ? 'border-dashed border-edge opacity-70' : 'border-edge'
          )}
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-mono text-[11px] font-semibold">
              Provider 1
            </span>
            <span
              className={cn(
                'rounded-md px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase',
                open
                  ? 'bg-foreground text-background'
                  : 'border border-edge text-muted-foreground'
              )}
            >
              {open ? 'open · skipped' : 'closed · serving'}
            </span>
          </div>

          <div className="mt-3 flex items-center gap-1" aria-hidden>
            {Array.from({ length: WINDOW }, (_, i) => {
              const slot = windowSlice[i];
              const failed = slot?.to === 1 && slot.fail;
              const okP1 = slot?.to === 1 && !slot.fail;
              return (
                <span
                  key={`${t}-${String(i)}`}
                  className={cn(
                    'h-3 w-full max-w-5',
                    failed
                      ? 'bg-foreground'
                      : okP1
                        ? 'bg-muted-foreground/40'
                        : 'bg-muted'
                  )}
                />
              );
            })}
          </div>
          <p className="mt-2 font-mono text-[10px] text-muted-foreground tabular-nums">
            rolling 60s window · fails {failsInWindow}/3
          </p>
        </div>

        {/* Provider 2 */}
        <div
          className={cn(
            'rounded-lg border p-3 transition-colors',
            open ? 'border-foreground' : 'border-edge opacity-70'
          )}
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-mono text-[11px] font-semibold">
              Provider 2
            </span>
            <span
              className={cn(
                'rounded-md px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase',
                open
                  ? 'bg-foreground text-background'
                  : 'border border-edge text-muted-foreground'
              )}
            >
              {open ? 'taking traffic' : 'standing by'}
            </span>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            {open
              ? 'Serving the same model while Provider 1 cools off.'
              : t > 6
                ? 'Provider 1 is eligible again: its failures scrolled out of the window.'
                : 'Next in the tier list. Nothing to warm up, nothing to reset.'}
          </p>
        </div>
      </div>
    </div>
  );
}
